import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ArtifactWithParameters, ArtifactParameter, EnhancedArtifact } from '../../types/artifacts';
import { ArtifactService } from '../../services/api/artifactService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import { artifactService } from '../../services/artifactService';
import { RootState } from '../../store';
import { ArtifactItemState, CreateArtifactItemProcess } from '../../types/artifactItems';
import {
  ProcessArtifactRecommendation,
  ProcessRecommendation,
  PeerArtifactRecommendation,
  StateRecommendation,
  TransitionRecommendation,
} from '../../types/recommendation';

interface SimpleChildArtifactRecommendation {
  id: string;
  name: string;
  description: string;
  artifact_type_id: string;
  reason: string;
  confidence_score: number;
  source_artifact_id: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface _SimpleProcessRecommendation {
  id: string;
  name: string;
  description: string;
  reason: string;
  confidence_score: number;
  source_artifact_id: string;
}

interface ArtifactState {
  artifacts: EnhancedArtifact[];
  hierarchy: EnhancedArtifact[];
  currentArtifact: EnhancedArtifact | null;
  loading: boolean;
  /** True while fetching a single artifact for the details panel (must not hide the tree). */
  currentArtifactLoading: boolean;
  error: string | null;
  rootStatus: { hasRoot: boolean } | null;
  parameters: ArtifactParameter[];
  recommendedChildArtifacts: SimpleChildArtifactRecommendation[];
  recommendedProcessArtifacts: ProcessArtifactRecommendation[];
  recommendedParameters: ArtifactParameter[];
  recommendedStates: StateRecommendation[];
  recommendedTransitions: TransitionRecommendation[];
  recommendedProcesses: ProcessRecommendation[];
  recommendedPeerArtifacts: PeerArtifactRecommendation[];
}

const initialState: ArtifactState = {
  artifacts: [],
  hierarchy: [],
  currentArtifact: null,
  loading: false,
  currentArtifactLoading: false,
  error: null,
  rootStatus: null,
  parameters: [],
  recommendedChildArtifacts: [],
  recommendedProcessArtifacts: [],
  recommendedParameters: [],
  recommendedStates: [],
  recommendedTransitions: [],
  recommendedProcesses: [],
  recommendedPeerArtifacts: [],
};

const artifactSlice = createSlice({
  name: 'artifact',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchArtifacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArtifacts.fulfilled, (state, action) => {
        state.loading = false;
        state.artifacts = action.payload.artifacts;
        state.hierarchy = action.payload.hierarchy;
      })
      .addCase(fetchArtifacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch artifacts';
      })
      .addCase(fetchArtifact.pending, (state) => {
        state.currentArtifactLoading = true;
        state.error = null;
      })
      .addCase(fetchArtifact.fulfilled, (state, action) => {
        state.currentArtifactLoading = false;
        state.currentArtifact = action.payload;
      })
      .addCase(fetchArtifact.rejected, (state, action) => {
        state.currentArtifactLoading = false;
        state.error = action.error.message || 'Failed to fetch artifact';
      })
      .addCase(checkRootStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkRootStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.rootStatus = action.payload;
      })
      .addCase(checkRootStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to check root status';
      })
      .addCase(fetchRecommendedChildArtifacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedChildArtifacts.fulfilled, (state, action) => {
        state.recommendedChildArtifacts = action.payload;
        state.loading = false;
      })
      .addCase(fetchRecommendedChildArtifacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch recommended child artifacts';
      })
      .addCase(fetchRecommendedProcessArtifacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedProcessArtifacts.fulfilled, (state, action) => {
        state.recommendedProcesses = action.payload.map((rec) => ({
          name: rec.name,
          description: rec.description,
          reason: rec.reason,
          confidence_score: rec.confidence_score,
          source_artifact_id: rec.source_artifact_id,
          artifact_id: rec.process_id,
          duration: 0,
          capacity: undefined,
          prime_process_id: rec.prime_process_id,
        }));
        state.loading = false;
      })
      .addCase(fetchRecommendedProcessArtifacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch recommended process artifacts';
      })
      .addCase(fetchRecommendedStates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedStates.fulfilled, (state, action) => {
        state.recommendedStates = action.payload;
        state.loading = false;
      })
      .addCase(fetchRecommendedStates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch recommended states';
      })
      .addCase(fetchRecommendedTransitions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedTransitions.fulfilled, (state, action) => {
        state.recommendedTransitions = action.payload;
        state.loading = false;
      })
      .addCase(fetchRecommendedTransitions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch recommended transitions';
      })
      .addCase(fetchRecommendedProcesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedProcesses.fulfilled, (state, action) => {
        state.loading = false;
        state.recommendedProcesses = action.payload.map((rec) => ({
          ...rec,
          prime_process_id: rec.prime_process_id || '',
          duration: rec.duration || 0,
          capacity: rec.capacity || 0,
        }));
      })
      .addCase(fetchRecommendedProcesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch process recommendations';
      })
      .addCase(fetchRecommendedPeerArtifacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecommendedPeerArtifacts.fulfilled, (state, action) => {
        state.recommendedPeerArtifacts = action.payload.map((rec) => ({
          id: rec.source_artifact_id,
          name: rec.name,
          description: rec.description,
          reason: rec.reason,
          confidence_score: rec.confidence_score,
          source_artifact_id: rec.source_artifact_id,
          artifact_type_id: rec.artifact_type_id,
          relationship_type: 'peer',
        }));
        state.loading = false;
      })
      .addCase(fetchRecommendedPeerArtifacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch recommended peer artifacts';
      })
      .addCase(updateArtifactState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateArtifactState.fulfilled, (state, _action) => {
        state.loading = false;
        if (state.currentArtifact) {
          state.currentArtifact = {
            ...state.currentArtifact,
            updated_at: new Date().toISOString(),
          };
        }
      })
      .addCase(updateArtifactState.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update artifact state';
      })
      .addCase(createProcessFromRecommendation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProcessFromRecommendation.fulfilled, (state) => {
        state.loading = false;
        if (state.currentArtifact) {
          state.currentArtifact = {
            ...state.currentArtifact,
            updated_at: new Date().toISOString(),
          };
        }
      })
      .addCase(createProcessFromRecommendation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create process from recommendation';
      });
  },
});

export const fetchArtifacts = createAsyncThunk(
  'artifacts/fetchArtifacts',
  async ({
    treeId,
    businessModelId,
    companyId,
  }: {
    treeId: string;
    businessModelId?: string;
    companyId?: string;
  }) => {
    // Get flat list of artifacts for general use
    const artifacts = await ArtifactService.getAll(treeId, businessModelId, companyId);
    
    // Get nested hierarchy structure for tree display
    // Use the hierarchy endpoint which returns nested structure with children arrays
    const hierarchy = await ArtifactService.getHierarchy(companyId, treeId);
    
    return { artifacts, hierarchy };
  }
);

export const fetchArtifact = createAsyncThunk('artifacts/fetchArtifact', async (id: string) => {
  return ArtifactService.getById(id);
});

export const createArtifact = createAsyncThunk(
  'artifacts/createArtifact',
  async (data: Partial<ArtifactWithParameters>) => {
    return await ArtifactService.create(data);
  }
);

export const updateArtifact = createAsyncThunk(
  'artifacts/updateArtifact',
  async (
    { id, data }: { id: string; data: Partial<ArtifactWithParameters> | any },
    { dispatch, getState }
  ) => {
    const updatedArtifact = await ArtifactService.update(id, data);
    const state = getState() as RootState;
    const treeId = data.artifact_tree_id || state.artifacts.currentArtifact?.artifact_tree_id;
    if (treeId) {
      await Promise.all([dispatch(fetchArtifact(id)), dispatch(fetchArtifacts({ treeId }))]);
    } else {
      await dispatch(fetchArtifact(id));
    }
    return updatedArtifact;
  }
);

export const deleteArtifact = createAsyncThunk('artifacts/deleteArtifact', async (id: string) => {
  await ArtifactService.delete(id);
  return id;
});

export const fetchParameters = createAsyncThunk(
  'artifacts/fetchParameters',
  async (artifact_id: string) => {
    return await ArtifactService.getParameters(artifact_id);
  }
);

export const createParameter = createAsyncThunk(
  'artifacts/createParameter',
  async ({ artifact_id, data }: { artifact_id: string; data: Partial<ArtifactParameter> }) => {
    return await ArtifactService.createParameter(artifact_id, data);
  }
);

export const updateParameter = createAsyncThunk(
  'artifacts/updateParameter',
  async ({
    artifact_id,
    parameter_id,
    data,
  }: {
    artifact_id: string;
    parameter_id: string;
    data: Partial<ArtifactParameter>;
  }) => {
    return await ArtifactService.updateParameter(artifact_id, parameter_id, data);
  }
);

export const deleteParameter = createAsyncThunk(
  'artifacts/deleteParameter',
  async ({ artifact_id, parameter_id }: { artifact_id: string; parameter_id: string }) => {
    await ArtifactService.deleteParameter(artifact_id, parameter_id);
    return { artifact_id, parameter_id };
  }
);

export const checkRootStatus = createAsyncThunk(
  'artifacts/checkRootStatus',
  async ({ treeId, companyId }: { treeId: string; companyId?: string }) => {
    return await ArtifactService.getRootStatus(treeId, companyId);
  }
);

export const createRootArtifact = createAsyncThunk(
  'artifacts/createRootArtifact',
  async ({ prime_artifact_id, treeId }: { prime_artifact_id: string; treeId: string }) => {
    const primeArtifact = await PrimeArtifactService.getById(prime_artifact_id);
    if (!primeArtifact.artifact_type_id) {
      throw new Error('Prime artifact must have a valid artifact type');
    }
    const artifact = await ArtifactService.create({
      name: primeArtifact.name,
      description: primeArtifact.description,
      artifact_type_id: primeArtifact.artifact_type_id,
      prime_artifact_id: prime_artifact_id,
      artifact_tree_id: treeId,
      parent_ref: null,
    });
    return artifact;
  }
);

export const fetchRecommendedArtifacts = createAsyncThunk(
  'artifacts/fetchRecommendedArtifacts',
  async (_artifactId: string) => {
    // TODO: Implement recommendation service
    return [];
  }
);

export const createRecommendedArtifact = createAsyncThunk(
  'artifacts/createRecommendedArtifact',
  async ({ artifactId, prime_artifact_id }: { artifactId: string; prime_artifact_id: string }) => {
    const primeArtifact = await PrimeArtifactService.getById(prime_artifact_id);
    return await ArtifactService.create({
      prime_artifact_id: prime_artifact_id,
      parent_ref: artifactId,
      name: 'Recommended Artifact',
      description: 'Artifact created from recommendation',
      artifact_type_id: primeArtifact.artifact_type_id,
    });
  }
);

export const fetchRecommendedParameters = createAsyncThunk(
  'artifacts/fetchRecommendedParameters',
  async (_artifactId: string) => {
    // TODO: Implement recommendation service
    return [];
  }
);

export const createRecommendedParameter = createAsyncThunk(
  'artifacts/createRecommendedParameter',
  async ({ artifactId, primeParameterId }: { artifactId: string; primeParameterId: string }) => {
    return await ArtifactService.createParameter(artifactId, {
      prime_parameter_id: primeParameterId,
    });
  }
);

export const createFromPrime = createAsyncThunk(
  'artifacts/createFromPrime',
  async ({
    prime_artifact_id,
    treeId,
    parentRef,
  }: {
    prime_artifact_id: string;
    treeId: string;
    parentRef?: string;
  }) => {
    const primeArtifact = await PrimeArtifactService.getById(prime_artifact_id);
    if (!primeArtifact.artifact_type_id) {
      throw new Error('Prime artifact must have a valid artifact type');
    }
    const artifact = await ArtifactService.create({
      name: primeArtifact.name,
      description: primeArtifact.description,
      artifact_type_id: primeArtifact.artifact_type_id,
      prime_artifact_id: prime_artifact_id,
      artifact_tree_ref: treeId,
      parent_ref: parentRef || null,
    });
    return artifact;
  }
);

export const addParameter = createAsyncThunk(
  'artifacts/addParameter',
  async ({ artifactId, primeParameterId }: { artifactId: string; primeParameterId: string }) => {
    return await ArtifactService.createParameter(artifactId, {
      prime_parameter_id: primeParameterId,
    });
  }
);

export const fetchRecommendedChildArtifacts = createAsyncThunk(
  'artifact/fetchRecommendedChildArtifacts',
  async (id: string) => {
    const response = await artifactService.getRecommendedChildArtifacts(id);
    return response;
  }
);

export const fetchRecommendedProcessArtifacts = createAsyncThunk(
  'artifacts/fetchRecommendedProcessArtifacts',
  async (artifactId: string) => {
    const response = await artifactService.getRecommendedProcessArtifacts(artifactId);
    return response as ProcessArtifactRecommendation[];
  }
);

export const fetchRecommendedStates = createAsyncThunk(
  'artifact/fetchRecommendedStates',
  async (id: string) => {
    const response = await artifactService.getRecommendedStates(id);
    return response;
  }
);

export const fetchRecommendedTransitions = createAsyncThunk(
  'artifact/fetchRecommendedTransitions',
  async (id: string) => {
    const response = await artifactService.getRecommendedTransitions(id);
    return response;
  }
);

export const fetchRecommendedProcesses = createAsyncThunk(
  'artifacts/fetchRecommendedProcesses',
  async (artifactId: string) => {
    const response = await artifactService.getRecommendedProcesses(artifactId);
    return response as ProcessRecommendation[];
  }
);

export const fetchRecommendedPeerArtifacts = createAsyncThunk(
  'artifacts/fetchRecommendedPeerArtifacts',
  async (artifactId: string) => {
    const response = await artifactService.getRecommendedPeerArtifacts(artifactId);
    return response as PeerArtifactRecommendation[];
  }
);

export const updateArtifactState = createAsyncThunk(
  'artifacts/updateArtifactState',
  async ({ artifactId, state }: { artifactId: string; state: ArtifactItemState }) => {
    const response = await ArtifactService.updateState(artifactId, state);
    return response;
  }
);

export const createProcessFromRecommendation = createAsyncThunk(
  'artifact/createProcessFromRecommendation',
  async ({ artifactId, process }: { artifactId: string; process: CreateArtifactItemProcess }) => {
    const response = await artifactService.createProcessFromRecommendation(artifactId, process);
    return response;
  }
);

export default artifactSlice.reducer;
